import React from "react";
import {
  Table,
  InputNumber,
  Input,
  DatePicker,
  Space,
  Button,
  Empty,
} from "antd";
import { CloseOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { RawMaterialPurchaseItem } from "../../../Types";

interface Props {
  items: RawMaterialPurchaseItem[];
  readOnly: boolean;
  onUpdate: (
    index: number,
    key: keyof RawMaterialPurchaseItem,
    val: any,
  ) => void;
  onRemove: (index: number) => void;
}

export const PurchaseItemsEditor: React.FC<Props> = ({
  items,
  readOnly,
  onUpdate,
  onRemove,
}) => {
  if (items.length === 0) {
    return (
      <div className="py-10 border border-dashed border-gray-200 rounded-md flex flex-col items-center justify-center bg-gray-50">
        <ShoppingCartOutlined
          style={{ fontSize: "24px" }}
          className="text-gray-300 mb-2"
        />
        <span className="text-xs text-gray-400">No items added yet.</span>
      </div>
    );
  }

  const columns = [
    {
      title: "Material",
      key: "raw_material",
      render: (_: any, rec: RawMaterialPurchaseItem) => (
        <div>
          <span className="font-medium text-gray-800 text-xs">
            {rec.raw_material_name}
          </span>
          <div className="text-[11px] text-gray-400">
            {rec.raw_material_code}
          </div>
        </div>
      ),
    },
    {
      title: "Unit Cost (Rs.)",
      key: "cost_price",
      render: (_: any, rec: RawMaterialPurchaseItem, idx: number) => (
        <InputNumber
          size="small"
          min={0}
          value={rec.cost_price}
          disabled={readOnly}
          onChange={(v) => onUpdate(idx, "cost_price", v || 0)}
          className="w-24"
        />
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (_: any, rec: RawMaterialPurchaseItem, idx: number) => (
        <Space>
          <InputNumber
            size="small"
            min={0.001}
            value={rec.quantity}
            disabled={readOnly}
            onChange={(v) => onUpdate(idx, "quantity", v || 0)}
            className="w-20"
          />
          <span className="text-[11px] text-gray-400 capitalize">
            {rec.raw_material_unit}
          </span>
        </Space>
      ),
    },
    {
      title: "Discount (%)",
      key: "discount_percent",
      render: (_: any, rec: RawMaterialPurchaseItem, idx: number) => (
        <InputNumber
          size="small"
          min={0}
          max={100}
          value={rec.discount_percent}
          disabled={readOnly}
          onChange={(v) => onUpdate(idx, "discount_percent", v || 0)}
          className="w-16"
        />
      ),
    },
    {
      title: "Batch No",
      key: "batch_no",
      render: (_: any, rec: RawMaterialPurchaseItem, idx: number) => (
        <Input
          size="small"
          value={rec.batch_no}
          disabled={readOnly}
          placeholder="New batch name"
          onChange={(e) => onUpdate(idx, "batch_no", e.target.value)}
          className="w-28 text-xs"
        />
      ),
    },
    {
      title: "Expiry Date",
      key: "expiry_date",
      render: (_: any, rec: RawMaterialPurchaseItem, idx: number) => (
        <DatePicker
          size="small"
          format="YYYY-MM-DD"
          disabled={readOnly}
          placeholder="Optional"
          value={rec.expiry_date ? dayjs(rec.expiry_date) : null}
          onChange={(date) =>
            onUpdate(
              idx,
              "expiry_date",
              date ? date.format("YYYY-MM-DD") : null,
            )
          }
          className="w-28 text-xs"
        />
      ),
    },
    {
      title: "Subtotal",
      dataIndex: "total",
      key: "total",
      render: (v: number) => (
        <span className="font-semibold text-gray-700 text-xs">
          Rs. {v.toFixed(2)}
        </span>
      ),
    },
    ...(!readOnly
      ? [
          {
            title: "",
            key: "delete",
            width: 40,
            render: (_: any, __: any, idx: number) => (
              <Button
                type="text"
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onRemove(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      dataSource={items}
      columns={columns}
      pagination={false}
      size="small"
      rowKey={(rec, idx) => rec.raw_material_id || `${idx}`}
      className="rounded-md border border-gray-100 overflow-hidden"
      scroll={{ x: 800 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No items added yet."
          />
        ),
      }}
    />
  );
};
